import type { PartialRecursive } from '../abstract/_';
import { AbstractCollection } from '../abstract/Collection';
import { AbstractDocument, Identifiable, Timestampable, Untrackable } from '../abstract/Document';
import type { IWedding } from './Weddings.types';
import { Badges } from './Weddings/Badges';
import { Bookmarks } from './Weddings/Bookmarks';
import { Costs } from './Weddings/Costs';
import { Events } from './Weddings/Events';
import { FeedbackCollection } from './Weddings/Feedback';
import { Files } from './Weddings/Files';
import { Guests } from './Weddings/Guests';
import { Interactions } from './Weddings/Interactions';
import { Keywords } from './Weddings/Keywords';
import { Notes } from './Weddings/Notes';
import { Photos } from './Weddings/Photos';
import { Preferences } from './Weddings/Preferences';
import { Subscriptions } from './Weddings/Subscriptions';
import type { IWeddingSubscription } from './Weddings/Subscriptions.types';
import { Suppliers } from './Weddings/Suppliers';
import { Tasks } from './Weddings/Tasks';
import { Users } from './Weddings/Users';
import type { Market } from '@bridebook/toolbox/src/gazetteer';
import { deleteField } from 'firebase/firestore';
import { pathOr } from 'ramda';
import { Campaigns } from './Weddings/Campaigns';

@Identifiable
@Timestampable
@Untrackable
export class Wedding extends AbstractDocument<IWedding> {
  readonly collections = {
    Badges: new Badges(this),
    Bookmarks: new Bookmarks(this),
    Campaigns: new Campaigns(this),
    Costs: new Costs(this),
    Events: new Events(this),
    Feedback: new FeedbackCollection(this),
    Files: new Files(this),
    Guests: new Guests(this),
    Interactions: new Interactions(this),
    Keywords: new Keywords(this),
    Notes: new Notes(this),
    Photos: new Photos(this),
    Preferences: new Preferences(this),
    Subscriptions: new Subscriptions(this),
    Suppliers: new Suppliers(this),
    Tasks: new Tasks(this),
    Users: new Users(this),
  };

  /**
   * Returns the country code of this wedding, falling back to `GB`.
   */
  get country() {
    return this.getCountry();
  }

  /**
   * Returns the country code of this wedding, falling back to `GB`.
   */
  async getCountry() {
    return pathOr('GB', ['l10n', 'country'], await this.get()) as string;
  }

  /**
   * Get Premium Document from subscriptions subcollection
   */
  getPremium() {
    return this.collections.Subscriptions.getPremium();
  }

  /**
   * @deprecated Use `Suppliers.getShortlisted('venue')` instead.
   */
  getShortlistedVenues() {
    return this.collections.Suppliers.getShortlisted('venue');
  }

  /**
   * Set Premium Document from subscriptions subcollection
   */
  setPremium(premium: PartialRecursive<IWeddingSubscription>) {
    return this.collections.Subscriptions.setPremium(premium);
  }

  /**
   * Updates the wedding country: updates wedding.l10n.country field and sets wedding.location to the new country.
   * @param {Market} market - the new wedding market
   */
  updateCountry(market: Market) {
    return this.set(
      {
        l10n: { country: market.country },
        location: {
          adminArea: deleteField(),
          city: deleteField(),
          country: market.country,
          name: market.getCountryName(),
          postalCode: deleteField(),
          provider: deleteField(),
          street: deleteField(),
        },
      },
      true,
    );
  }
}

export class Weddings extends AbstractCollection<Wedding, IWedding> {
  static default = {
    guests: {
      categories: {
        data: {
          '00000000-0000-4000-8000-000000000001': 'me',
          '00000000-0000-4000-8000-000000000002': 'partner',
        },
        sort: ['00000000-0000-4000-8000-000000000001', '00000000-0000-4000-8000-000000000002'],
      },
      count: 0,
    } as IWedding['guests'],
  };

  static path = 'weddings';

  constructor() {
    super(Weddings.path, Wedding);
  }

  static get _() {
    return new this();
  }
}
